import React, { useEffect, useState } from 'react';
import { useParams, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import '../styles/ProjectDetail.css';
import API_BASE from '../utils/api.js';

function getApiBase() {
  return API_BASE;
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (!projectId) {
      setError('No project id in route');
      setLoading(false);
      return;
    }
    const userId = localStorage.getItem('userId');
    if (!userId) {
      setError('No user signed in');
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const signal = controller.signal;

    async function fetchProject() {
      setLoading(true);
      setError(null);
      const base = getApiBase();
      const candidate1 = `${base}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`;
      const candidate2 = `${base}/api/projects/${encodeURIComponent(userId)}`;

      try {
        let got = null;
        let res = await fetch(candidate1, { signal, cache: 'no-store' });
        if (res.ok && (res.headers.get('content-type') || '').includes('application/json')) {
          got = await res.json();
        } else {
          res = await fetch(candidate2, { signal, cache: 'no-store' });
          if (!res.ok) throw new Error(`Server returned ${res.status} for ${candidate2}`);
          const json = await res.json();
          let arr = Array.isArray(json)
            ? json
            : json.Items || json.items || Object.values(json).find((v) => Array.isArray(v)) || [];
          if (!Array.isArray(arr)) arr = [json];
          got = arr.find(
            (it) =>
              it.projectId === projectId ||
              it.id === projectId ||
              (it.projectNickname && encodeURIComponent(it.projectNickname) === projectId) ||
              (it.name && encodeURIComponent(it.name) === projectId)
          );
        }
        if (!got) throw new Error('Project not found');
        setProject(got);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('fetchProject error', err);
          setError(err.message || 'Failed to load project');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchProject();
    return () => controller.abort();
  }, [projectId, location.state?.refresh]);

  useEffect(() => {
    if (location.state?.refresh) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  if (loading) return <div className="project-detail-page">Loading project…</div>;
  if (error)
    return (
      <div className="project-detail-page">
        <div className="error">Error: {error}</div>
        <button onClick={() => navigate(-1)} className="btn">
          Go back
        </button>
      </div>
    );

  const projectName =
    project?.projectNickname || project?.name || project?.title || 'Untitled Project';
  const projectAddress = project?.projectStreet
    ? `${project.projectStreet}, ${project.projectCity}, ${project.projectState} ${project.projectZip}`
    : project?.projectSite || '';
  const tabClass = ({ isActive }) => (isActive ? 'tab active' : 'tab');

  return (
    <div className="project-detail-page">
      <div className="project-top">
        <div className="left-col">
          <div className="project-title-row">
            <h1 className="project-title">{projectName}</h1>

            <button
              className="icon-edit"
              title="Edit project"
              onClick={() =>
                navigate(`/projects/${encodeURIComponent(projectId)}/edit`, {
                  state: { project },
                })
              }
            >
              ✎
            </button>
          </div>
          <div className="project-subtitle">{projectAddress || '—'}</div>

          <div className="project-tabs">
            <NavLink to={`/projects/${encodeURIComponent(projectId)}`} end className={tabClass}>
              Project Details
            </NavLink>
            <NavLink to={`/projects/${encodeURIComponent(projectId)}/library`} className={tabClass}>
              Library
            </NavLink>
            <NavLink to={`/projects/${encodeURIComponent(projectId)}/files`} className={tabClass}>
              Files
            </NavLink>
            <NavLink
              to={`/projects/${encodeURIComponent(projectId)}/proposals`}
              className={tabClass}
            >
              Proposals
            </NavLink>
            <NavLink
              to={`/projects/${encodeURIComponent(projectId)}/invoices`}
              className={tabClass}
            >
              Invoices
            </NavLink>
            <NavLink
              to={`/projects/${encodeURIComponent(projectId)}/purchase-orders`}
              className={tabClass}
            >
              Purchase Orders
            </NavLink>
            <NavLink
              to={`/projects/${encodeURIComponent(projectId)}/retainers`}
              className={tabClass}
            >
              Retainers
            </NavLink>
            <NavLink to={`/projects/${encodeURIComponent(projectId)}/credits`} className={tabClass}>
              Credits
            </NavLink>
            <NavLink
              to={`/projects/${encodeURIComponent(projectId)}/schedule`}
              className={tabClass}
            >
              Schedule
            </NavLink>
            <NavLink
              to={`/projects/${encodeURIComponent(projectId)}/calendar`}
              className={tabClass}
            >
              Calendar
            </NavLink>
            <NavLink to={`/projects/${encodeURIComponent(projectId)}/todo`} className={tabClass}>
              To Do List
            </NavLink>
            <NavLink to={`/projects/${encodeURIComponent(projectId)}/chat`} className={tabClass}>
              Chat
            </NavLink>
          </div>
        </div>

        <div className="right-actions"></div>
      </div>

      {/* outlet provides project + projectId context to nested routes */}
      <div className="project-nested-content">
        <Outlet context={{ project, projectId, setProject }} />
      </div>
    </div>
  );
}
